import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppState } from '@root/app-state.module';
import { select, Store } from '@ngrx/store';
import {
  selectNumberOfAllSelectedLayers,
  selectSelectedBoundaryById,
  selectSelectedTrendsLayers
} from '@store/map-layers.selector';
import {
  FAULT_LINE_LEGEND,
  FLOOD_ZONE_LEGEND,
  LayerId,
  PLANNED_LAND_USE_LEGEND,
  TRENDS_TYPE_NONE
} from '@root/search/models/map.constants';
import { LayerInfo, RangeInfo } from '@root/search/models/map.models';
import { SetTrendsLayer, ToggleBoundary } from '@store/search-board/search-board.actions';
import {
  selectMapLegendData,
  selectSidePanelState
} from '@store/search-board/search-board.selector';
import { SidePanelTab, SidePanelView } from '@store/search-board/search-board.model';
import { MixpanelService } from '@shared/services/mixpanel.service';
import { SearchMixpanelUtil } from '@root/search/search-mixpanel-util';

@Component({
  selector: 'rlst-map-legend',
  templateUrl: './map-legend.component.html',
  styleUrls: ['./map-legend.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MapLegendComponent implements OnInit {
  public floodZoneLayer$: Observable<LayerInfo>;
  public faultLineLayer$: Observable<LayerInfo>;
  public plannedLandUseLayer$: Observable<LayerInfo>;

  public trendsLayer$: Observable<LayerInfo>;
  public trendsLegendData$: Observable<Array<RangeInfo>>;

  public readonly floodZoneLegend: Array<RangeInfo> = FLOOD_ZONE_LEGEND;
  public readonly faultLineLegend: Array<RangeInfo> = FAULT_LINE_LEGEND;
  public readonly plannedLandUseLegend: Array<RangeInfo> = PLANNED_LAND_USE_LEGEND;

  public hasSelectedLayers$: Observable<boolean>;

  private sidePanelTabOpened: SidePanelTab;
  private readonly searchMixpanelUtil: SearchMixpanelUtil = new SearchMixpanelUtil(this.store$);

  constructor(private readonly store$: Store<AppState>,
              private readonly mixpanelService: MixpanelService) {}

  ngOnInit(): void {
    this.floodZoneLayer$ = this.store$.pipe(select(selectSelectedBoundaryById, LayerId.FLOOD_ZONE_LAYER));
    this.faultLineLayer$ = this.store$.pipe(select(selectSelectedBoundaryById, LayerId.FAULT_LINE_LAYER));
    this.plannedLandUseLayer$ = this.store$.pipe(select(selectSelectedBoundaryById, LayerId.PLANNED_LAND_USE_LAYER));

    this.hasSelectedLayers$ = this.store$.pipe(
      select(selectNumberOfAllSelectedLayers),
      map((numberOfAllSelectedLayers: number) => numberOfAllSelectedLayers > 0)
    );

    this.trendsLayer$ = this.store$.pipe(
      select(selectSelectedTrendsLayers),
      map(([trendsLayer]) => trendsLayer)
    );
    this.trendsLegendData$ = this.store$.pipe(select(selectMapLegendData));

    this.store$
      .pipe(
        select(selectSidePanelState)
      )
      .subscribe((sidePanelView: SidePanelView) => {
        this.sidePanelTabOpened = sidePanelView.sidePanelTabOpened;
      });
  }

  public closeBoundaryLayer(layer: LayerInfo): void {
    return this.store$.dispatch(new ToggleBoundary(layer));
  }

  public closeTrendLayer(): void {
    this.logMapTrendsMixPanel('Search - Trend Removed');
    return this.store$.dispatch(new SetTrendsLayer(TRENDS_TYPE_NONE));
  }

  private logMapTrendsMixPanel(eventId: string) {
    this.searchMixpanelUtil.setupMixPanelItems(this.sidePanelTabOpened);

    this.store$.select(selectSelectedTrendsLayers).forEach((layersInfo) => {
      if (layersInfo != null) {
        layersInfo.forEach((layerInfo) => {
          this.mixpanelService.track(eventId, {
            countyList: this.searchMixpanelUtil.countyList,
            searchType: this.sidePanelTabOpened,
            isSavedSearch: (this.sidePanelTabOpened === 'quickSearch') ? false :
              this.searchMixpanelUtil.selectedSearch !== 'My Search Default',
            searchParameters: this.searchMixpanelUtil.searchFieldNames.join('; '),
            trendLayer: layerInfo.label
          });
        });
      } else {
        this.mixpanelService.track(eventId, {
          countyList: this.searchMixpanelUtil.countyList,
          searchType: this.sidePanelTabOpened,
          isSavedSearch: (this.sidePanelTabOpened === 'quickSearch') ? false :
            this.searchMixpanelUtil.selectedSearch !== 'My Search Default',
          searchParameters: this.searchMixpanelUtil.searchFieldNames.join('; '),
          trendLayer: 'Not available'
        });
      }
    });
  }
}
